import "react-datepicker/dist/react-datepicker.css";
import { Button, Card } from "react-bootstrap";
import { getConfigWithToken } from "../config/config";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  getFilteredSessions,
  getSortedSessions,
  getVerificationStatus,
  getPaymentMethod,
} from "../components/UpcomingSessions/utils";
import SessionFilters from "../components/UpcomingSessions/SessionFilters";
import SessionsTable from "../components/UpcomingSessions/SessionsTable";
import SessionPagination from "../components/UpcomingSessions/SessionPagination";
import SessionActionsDropdown from "../components/UpcomingSessions/SessionActionsDropdown";
import SessionDetailsDrawer from "../components/UpcomingSessions/SessionDetailsDrawer";
import VerifyPaymentModal from "../components/UpcomingSessions/VerifyPaymentModal";
import VerifyInsuranceModal from "../components/UpcomingSessions/VerifyInsuranceModal";
import CancelSessionModal from "../components/UpcomingSessions/CancelSessionModal";

const UpcomingSessions = () => {
  const [appointmentsPerPage, setAppointmentsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginateAppointments, setPaginateAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("All");
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState("All");
  const [datePreset, setDatePreset] = useState("All");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [paymentDataBySession, setPaymentDataBySession] = useState({});
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showInsuranceVerifyModal, setShowInsuranceVerifyModal] =
    useState(false);
  const [selectedSessionForCancel, setSelectedSessionForCancel] =
    useState(null);
  const [selectedSessionForVerify, setSelectedSessionForVerify] =
    useState(null);
  const [
    selectedSessionForInsuranceVerify,
    setSelectedSessionForInsuranceVerify,
  ] = useState(null);
  const [insuranceActionCount, setInsuranceActionCount] = useState(0);
  const [cancellationReason, setCancellationReason] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });
  const [networkError, setNetworkError] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const user = useSelector((state) => state?.user);

  const cancellationReasons = [
    "Emergency situation",
    "Health reasons",
    "Patient requested cancellation",
    "Provider unavailable",
    "Scheduling conflict",
    "Technical issues",
    "Weather conditions",
    "Other",
  ];

  const logAuditEvent = (event, sessionId, data = {}) => {
    const auditLog = {
      event,
      sessionId: sessionId || "N/A",
      timestamp: new Date().toISOString(),
      updatedBy: user?.user?._id || user?.user?.id || "admin_001",
      ...data,
    };
  };

  const clearSearchFilter = () => {
    setDebouncedSearch("");
    setSearch("");
  };

  const fetchSessions = useCallback(async () => {
    let timeoutId = null;
    try {
      setSessionsLoading(true);
      setNetworkError(false);

      const params = {};

      if (selectedStartDate && selectedEndDate) {
        const start = new Date(selectedStartDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(selectedEndDate);
        end.setHours(23, 59, 59, 999);

        params.endDate = end.toISOString().split("T")[0];
        params.startDate = start.toISOString().split("T")[0];
      } else if (selectedStartDate && !selectedEndDate) {
        const year = selectedStartDate.getFullYear();
        const day = String(selectedStartDate.getDate()).padStart(2, "0");
        const month = String(selectedStartDate.getMonth() + 1).padStart(2, "0");

        params.date = `${year}-${month}-${day}`;
      }

      if (debouncedSearch && debouncedSearch.trim() !== "") {
        params.search = debouncedSearch.trim();
        params.searchAll = true;
      }
      params.upcoming = true;
      params.limit = appointmentsPerPage;
      params.skip = (currentPage - 1) * appointmentsPerPage;
      params.skipStripe = true;

      timeoutId = setTimeout(() => {
        setSessionsLoading(false);
      }, 60000);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/getSessionHistory`,
        { params, ...getConfigWithToken(user.user?.token), timeout: 30000 },
      );

      if (response.data.success) {
        const sessionsData = response.data.data ?? [];
        const transformedSessions = sessionsData.map((session) => ({
          ...session,
          _id: session._id ?? session.id ?? Math.random().toString(),
          appointmentdate:
            session.appointmentdate ?? session.date ?? session.appointmenttime,
          appointmenttime:
            session.appointmenttime ?? session.appointmentdate ?? session.date,
          status: session.status ?? "Scheduled",
        }));

        setSessions(transformedSessions);
        const serverTotal =
          response.data.total ?? response.data.totalCount ?? null;
        setTotalSessions(
          Number.isFinite(serverTotal) ? serverTotal : transformedSessions.length
        );

        const paymentDataMap = {};
        transformedSessions.forEach((session) => {
          const sessionId = session._id || session.id;
          if (sessionId && session.paymentData) {
            paymentDataMap[sessionId] = session.paymentData;
          } else if (sessionId) {
            paymentDataMap[sessionId] = null;
          }
        });
        setPaymentDataBySession((prev) => ({ ...prev, ...paymentDataMap }));
        if (timeoutId) clearTimeout(timeoutId);
        setSessionsLoading(false);
      } else {
        if (timeoutId) clearTimeout(timeoutId);
        toast.error("Failed to fetch sessions.");
        setSessionsLoading(false);
      }
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      console.error("-> -> fetchSessions -> error->", error);
      if (error.code === "ECONNABORTED" || error.message === "Network Error") {
        setNetworkError(true);
        toast.error(
          "Network connection lost. Please check your connection and try again.",
        );
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to fetch sessions",
        );
      }
      setSessionsLoading(false);
    }
  }, [
    debouncedSearch,
    selectedStartDate,
    selectedEndDate,
    user.user?.token,
    currentPage,
    appointmentsPerPage,
  ]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleRangeSelect = (dates) => {
    if (!dates) {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setCurrentPage(1);
      return;
    }

    const [start, end] = dates;

    setSelectedEndDate(end);
    setSelectedStartDate(start);

    if (end) setShowDatePicker(false);
  };

  const handleViewDetails = async (session) => {
    try {
      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }
      if (session.paymentData && !paymentDataBySession[appointmentId]) {
        setPaymentDataBySession((prev) => ({
          ...prev,
          [appointmentId]: session.paymentData,
        }));
      }
      setSelectedSession(session);
      setShowDetailsDrawer(true);
      logAuditEvent("session_viewed", appointmentId);
    } catch (error) {
      console.error("Error in handleViewDetails:", error);
      toast.error("Error loading session details");
    }
  };
  const handleVerifyPayment = (session) => {
    const paymentMethod = getPaymentMethod(
      session,
      paymentDataBySession,
    )?.toLowerCase();
    if (paymentMethod === "insurance") {
      setSelectedSessionForInsuranceVerify(session);
      setShowInsuranceVerifyModal(true);
      return;
    }
    setSelectedSessionForVerify(session);
    setShowVerifyModal(true);
  };

  const handleVerifyInsurance = (session) => {
    setSelectedSessionForInsuranceVerify(session);
    setShowInsuranceVerifyModal(true);
  };

  const handlePaymentMethodChanged = async (resultData) => {
    try {
      const appointmentId =
        resultData?.appointmentId ||
        selectedSession?._id ||
        selectedSession?.id;

      if (appointmentId && resultData?.paymentMethod) {
        const method = resultData.paymentMethod;
        const isInsurance = method === "insurance";

        setPaymentDataBySession((prev) => ({
          ...prev,
          [appointmentId]: {
            ...(prev[appointmentId] || {}),
            paymentMethod: method,
            insuranceActive: false,
            insuranceEligibilityVerified: false,
            policyInactive: false,
            issueType: isInsurance ? prev[appointmentId]?.issueType || null : null,
            hasRemainingDeductible: isInsurance
              ? prev[appointmentId]?.hasRemainingDeductible || false
              : false,
          },
        }));
      }

      await fetchSessions();
      setInsuranceActionCount((prev) => prev + 1);
    } catch (error) {
      console.error("Error refreshing after payment method change:", error);
    }
  };

  const handleInsuranceVerifySuccess = async (resultData) => {
    try {
      const appointmentId =
        resultData?.appointmentId ||
        selectedSessionForInsuranceVerify?._id ||
        selectedSessionForInsuranceVerify?.id;
      const verification = resultData?.insuranceVerification;
      const previousStatus = selectedSessionForInsuranceVerify
        ? getVerificationStatus(
            selectedSessionForInsuranceVerify,
            paymentDataBySession,
          )
        : "Pending";

      if (appointmentId && verification) {
        setPaymentDataBySession((prev) => ({
          ...prev,
          [appointmentId]: {
            ...(prev[appointmentId] || {}),
            insuranceActive: verification.status === "verified",
            insuranceEligibilityVerified: verification.status === "verified",
            policyInactive: verification.status === "invalid",
            coverageStartDate: verification.coverageEffectiveDate || null,
            coverageEndDate: verification.coverageEndDate || null,
            hasRemainingDeductible: Boolean(
              verification.hasRemainingDeductible,
            ),
            remainingDeductibleBalance:
              verification.remainingDeductibleBalance || null,
            copay: verification.estimatedCopay || null,
            coinsurance: verification.estimatedCoinsurance || null,
            issueType: verification.issueType || null,
            internalNote: verification.internalNote || null,
            verifiedByName: verification.verifiedByName || null,
            verifiedBy: verification.verifiedBy || null,
            flaggedByName:
              verification.flaggedByName || verification.verifiedByName || null,
            flaggedBy:
              verification.flaggedBy || verification.verifiedBy || null,
            patientNotified: verification.patientNotified === true,
            lastVerifiedAt:
              verification.flaggedAt || verification.verifiedAt || null,
            verificationSource: verification.source || "Super Admin Panel",
          },
        }));
      }

      await fetchSessions();
      setInsuranceActionCount((prev) => prev + 1);

      logAuditEvent(
        verification?.status === "invalid"
          ? "insurance_invalidated"
          : "insurance_verified",
        appointmentId,
        {
          previousStatus,
          newStatus:
            verification?.status === "verified" ? "Verified" : "Invalid",
        },
      );

      setShowInsuranceVerifyModal(false);
      setSelectedSessionForInsuranceVerify(null);
    } catch (error) {
      console.error("Error refreshing after insurance verification:", error);
    }
  };
  const handleConfirmVerify = async () => {
    try {
      if (!selectedSessionForVerify) {
        toast.error("Session not found");
        return;
      }

      const appointmentId =
        selectedSessionForVerify._id || selectedSessionForVerify.id;
      const previousStatus = getVerificationStatus(
        selectedSessionForVerify,
        paymentDataBySession,
      );
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/payments/verify/${appointmentId}`,
        {},
        getConfigWithToken(user.user?.token),
      );

      if (response.data.success) {
        await fetchSessions();
        const newStatus = "Verified";

        logAuditEvent("session_verified", appointmentId, {
          previousStatus,
          newStatus: newStatus,
        });

        toast.success("Payment/Insurance verified successfully");
        setShowVerifyModal(false);
        setSelectedSessionForVerify(null);
      } else {
        toast.error(response.data.message || "Failed to verify payment");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      toast.error(error?.response?.data?.message || "Failed to verify payment");
    }
  };

  const handleCancelSession = (session) => {
    setSelectedSessionForCancel(session);
    setShowCancelModal(true);
  };
  const handleConfirmCancel = async () => {
    try {
      if (!selectedSessionForCancel) {
        toast.error("Session not found");
        return;
      }

      if (!cancellationReason) {
        toast.error("Please select a cancellation reason");
        return;
      }

      const appointmentId =
        selectedSessionForCancel._id || selectedSessionForCancel.id;
      const previousStatus = getVerificationStatus(
        selectedSessionForCancel,
        paymentDataBySession,
      );

      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "Canceled by Super Admin",
          cancellationReason,
        },
        getConfigWithToken(user.user?.token),
      );

      if (updateResponse.data.success) {
        logAuditEvent("session_canceled", appointmentId, {
          previousStatus,
          newStatus: "Canceled",
          cancellationReason,
        });

        toast.success("Session canceled successfully");
        setShowCancelModal(false);
        setSelectedSessionForCancel(null);
        setCancellationReason("");
        fetchSessions();
      } else {
        toast.error("Failed to cancel session");
      }
    } catch (error) {
      console.error("Error canceling session:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel session");
    }
  };

  const calculateDropdownPosition = useCallback(
    (buttonRect, dropdownHeight = 160, dropdownWidth = 160) => {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      let top, left;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = buttonRect.top - dropdownHeight - 4;
        if (top < 0) {
          top = 4;
        }
      } else {
        top = buttonRect.bottom + 4;
        if (top + dropdownHeight > viewportHeight) {
          top = viewportHeight - dropdownHeight - 4;
          if (top < buttonRect.top - dropdownHeight) {
            top = Math.max(4, buttonRect.top - dropdownHeight - 4);
          }
        }
      }

      left = buttonRect.left + buttonRect.width / 2;
      if (left - dropdownWidth / 2 < 0) {
        left = dropdownWidth / 2 + 4;
      } else if (left + dropdownWidth / 2 > viewportWidth) {
        left = viewportWidth - dropdownWidth / 2 - 4;
      }

      return { top, left };
    },
    [],
  );

  const handleDropdownToggle = useCallback(
    (sessionId, event) => {
      if (openDropdownId === sessionId) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      } else {
        const button = event.currentTarget;
        dropdownButtonRef.current = button;
        const rect = button.getBoundingClientRect();

        const position = calculateDropdownPosition(rect);
        setDropdownPosition(position);
        setOpenDropdownId(sessionId);
      }
    },
    [openDropdownId, calculateDropdownPosition],
  );

  const handleDropdownItemClick = (handler, session) => {
    handler(session);
    setOpenDropdownId(null);
    dropdownButtonRef.current = null;
  };
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    selectedStartDate,
    selectedEndDate,
    paymentMethodFilter,
    statusFilter,
    appointmentStatusFilter,
    appointmentsPerPage,
  ]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    const filtered = getFilteredSessions(
      sessions,
      debouncedSearch,
      statusFilter,
      paymentMethodFilter,
      paymentDataBySession,
      true,
      false,
      "All",
      appointmentStatusFilter,
    );
    const sorted = getSortedSessions(
      filtered,
      sortConfig,
      paymentDataBySession,
    );
    setPaginateAppointments(sorted);
  }, [
    sessions,
    statusFilter,
    paymentMethodFilter,
    appointmentStatusFilter,
    sortConfig,
    debouncedSearch,
    paymentDataBySession,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdownId &&
        !event.target.closest(".session-dropdown-button") &&
        !event.target.closest(".session-dropdown-menu")
      ) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && openDropdownId) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    let scrollTimeout;
    const handleScroll = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const rect = dropdownButtonRef.current.getBoundingClientRect();
          const position = calculateDropdownPosition(rect);
          setDropdownPosition(position);
        }, 10);
      }
    };

    const handleResize = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        const position = calculateDropdownPosition(rect);
        setDropdownPosition(position);
      }
    };

    if (openDropdownId) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
      return () => {
        clearTimeout(scrollTimeout);
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [openDropdownId, calculateDropdownPosition]);

  const filteredSessions = getFilteredSessions(
    sessions,
    debouncedSearch,
    statusFilter,
    paymentMethodFilter,
    paymentDataBySession,
    true,
    false,
    "All",
    appointmentStatusFilter,
  );
  const sortedSessions = getSortedSessions(
    filteredSessions,
    sortConfig,
    paymentDataBySession,
  );
  const totalPages = Math.ceil(totalSessions / appointmentsPerPage);
  const indexOfFirstSession =
    totalSessions > 0 ? (currentPage - 1) * appointmentsPerPage + 1 : 0;
  const indexOfLastSession =
    totalSessions > 0
      ? Math.min(currentPage * appointmentsPerPage, totalSessions)
      : 0;

  return (
    <div className="app-page session-history-container">
      <ToastContainer />

      {networkError && (
        <div
          style={{
            backgroundColor: "#FEE2E2",
            border: "1px solid #EF4444",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            color: "#991B1B",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Unable to load sessions. Please refresh.</span>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => {
              setNetworkError(false);
              fetchSessions();
            }}
          >
            Refresh
          </Button>
        </div>
      )}

      <Card
        style={{
          border: "none",
          borderRadius: "10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          height: "100%",
          overflow: "hidden",
          maxWidth: "100%",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Card.Body
          style={{
            overflow: "hidden",
            maxWidth: "100%",
            padding: "0",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div className="app-page-toolbar mb-0" style={{ flexWrap: "nowrap", minWidth: 0 }}>
            <h3 className="app-page-title" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              Upcoming Sessions
            </h3>

            <SessionFilters
              showDatePicker={showDatePicker}
              setShowDatePicker={setShowDatePicker}
              search={search}
              setSearch={setSearch}
              clearSearchFilter={clearSearchFilter}
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              handleRangeSelect={handleRangeSelect}
              paymentMethodFilter={paymentMethodFilter}
              setPaymentMethodFilter={setPaymentMethodFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              appointmentStatusFilter={appointmentStatusFilter}
              setAppointmentStatusFilter={setAppointmentStatusFilter}
              datePreset={datePreset}
              setDatePreset={setDatePreset}
              setCurrentPage={setCurrentPage}
            />
          </div>

          <div
            style={{
              width: "100%",
              maxWidth: "100%",
              marginTop: "20px",
              padding: "0",
              boxSizing: "border-box",
              minWidth: 0,
            }}
          >
            <SessionsTable
              sessionsLoading={sessionsLoading}
              paginateAppointments={paginateAppointments}
              sessions={sessions}
              sortConfig={sortConfig}
              handleSort={handleSort}
              handleDropdownToggle={handleDropdownToggle}
              paymentDataBySession={paymentDataBySession}
            />
            {totalSessions > 0 && (
              <SessionPagination
                appointmentsPerPage={appointmentsPerPage}
                setAppointmentsPerPage={setAppointmentsPerPage}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                totalPages={totalPages}
                totalSessions={totalSessions}
                indexOfFirstSession={indexOfFirstSession}
                indexOfLastSession={indexOfLastSession}
                paginate={paginate}
              />
            )}
          </div>
        </Card.Body>
      </Card>

      <SessionActionsDropdown
        openDropdownId={openDropdownId}
        dropdownPosition={dropdownPosition}
        dropdownMenuRef={dropdownMenuRef}
        paginateAppointments={paginateAppointments}
        handleDropdownItemClick={handleDropdownItemClick}
        handleViewDetails={handleViewDetails}
        handleVerifyPayment={handleVerifyPayment}
        handleCancelSession={handleCancelSession}
        paymentDataBySession={paymentDataBySession}
      />

      <SessionDetailsDrawer
        showDetailsDrawer={showDetailsDrawer}
        selectedSession={selectedSession}
        setShowDetailsDrawer={setShowDetailsDrawer}
        setSelectedSession={setSelectedSession}
        paymentDataBySession={paymentDataBySession}
        onVerifyInsurance={handleVerifyInsurance}
        insuranceActionCount={insuranceActionCount}
        onPaymentMethodChanged={handlePaymentMethodChanged}
      />

      <VerifyInsuranceModal
        show={showInsuranceVerifyModal}
        onHide={() => {
          setShowInsuranceVerifyModal(false);
          setSelectedSessionForInsuranceVerify(null);
        }}
        session={selectedSessionForInsuranceVerify}
        userToken={user.user?.token}
        onSuccess={handleInsuranceVerifySuccess}
      />

      <VerifyPaymentModal
        show={showVerifyModal}
        onHide={() => {
          setShowVerifyModal(false);
          setSelectedSessionForVerify(null);
        }}
        selectedSessionForVerify={selectedSessionForVerify}
        handleConfirmVerify={handleConfirmVerify}
        paymentDataBySession={paymentDataBySession}
      />

      <CancelSessionModal
        show={showCancelModal}
        onHide={() => {
          setShowCancelModal(false);
          setSelectedSessionForCancel(null);
          setCancellationReason("");
        }}
        selectedSessionForCancel={selectedSessionForCancel}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
        handleConfirmCancel={handleConfirmCancel}
        cancellationReasons={cancellationReasons}
      />
    </div>
  );
};

export default UpcomingSessions;
